import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import {
  getFocusedItemId,
  HaNContext,
  HaNViewStoreContext,
  selectSelection,
  useHaNView,
  usePeopleModel,
} from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { EmptyListPlaceholder } from "@molecules";

import { useFitNodeInView } from "../map/useFitNodeInView";
import { FloorSuggestionTile } from "../shared/FloorSuggestionTile";
import { HaNCellSkeleton } from "../shared/HaNCellSkeleton";
import { HaNSlider } from "../shared/HaNSlider/HaNSlider";
import { PersonCell } from "./PersonCell";
import { useSelectPerson } from "./useSelectPerson";

export const PeopleCarousel = () => {
  const { tab } = useRequiredContext(HaNContext);

  if (tab !== "people") return null;

  return <PeopleCarouselContent />;
};

const PeopleCarouselContent = () => {
  const { t } = useTranslation();
  const { isLoading, isFailed, onSelectedFloor, otherFloors } = usePeopleModel();
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const selectedId = useHaNView(state => selectSelection("people")(state)?.id ?? null);
  const selectPerson = useSelectPerson();
  const fitNodeInView = useFitNodeInView();

  if (isLoading) {
    return (
      <HaNSlider.Card className="items-center">
        <HaNCellSkeleton />
      </HaNSlider.Card>
    );
  }

  // The list has the retry
  if (isFailed) {
    return (
      <HaNSlider.Card>
        <EmptyListPlaceholder text={t("hereAndNow:PeopleLoadFailed")} />
      </HaNSlider.Card>
    );
  }

  const suggestions =
    onSelectedFloor.length > 0
      ? []
      : [otherFloors.find(f => f.direction === "above"), otherFloors.find(f => f.direction === "below")].filter(
          floorGroup => floorGroup !== undefined,
        );

  if (onSelectedFloor.length === 0 && suggestions.length === 0) {
    return (
      <HaNSlider.Card>
        <EmptyListPlaceholder text={t("hereAndNow:NobodyInBuilding")} />
      </HaNSlider.Card>
    );
  }

  const personIds = onSelectedFloor.map(row => row.owner.id);
  const focusedIndex = personIds.indexOf(getFocusedItemId(personIds, selectedId) ?? "");

  const focusSlide = (index: number) => {
    const row = onSelectedFloor[index];

    if (!row) return;

    const { nodeId } = row.reservation.workspace;

    viewStore.select("people", { kind: "person", id: row.owner.id, nodeId });

    fitNodeInView(nodeId);
  };

  return (
    <HaNSlider
      focusedIndex={focusedIndex}
      onFocusChange={focusSlide}>
      {onSelectedFloor.length > 0 ? (
        onSelectedFloor.map(row => {
          return (
            <HaNSlider.Slide key={row.owner.id}>
              <HaNSlider.CardButton onClick={() => selectPerson(row)}>
                <PersonCell row={row} />
              </HaNSlider.CardButton>
            </HaNSlider.Slide>
          );
        })
      ) : (
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <EmptyListPlaceholder
              text={t("hereAndNow:NobodyOnFloor")}
              className="justify-center p-2 [&>svg]:size-14"
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>
      )}
      {suggestions.map(({ floorId, direction, people }) => {
        return (
          <Fragment key={floorId}>
            <HaNSlider.Slide>
              <HaNSlider.Card>
                <FloorSuggestionTile
                  floorId={floorId}
                  direction={direction}
                />
              </HaNSlider.Card>
            </HaNSlider.Slide>

            {people.map(row => {
              return (
                <HaNSlider.Slide key={row.owner.id}>
                  <HaNSlider.CardButton onClick={() => selectPerson(row)}>
                    <PersonCell row={row} />
                  </HaNSlider.CardButton>
                </HaNSlider.Slide>
              );
            })}
          </Fragment>
        );
      })}
    </HaNSlider>
  );
};
